/**
 * Pattern Suggestion Badge & Panel
 * =================================
 * 
 * UI components for displaying and acting on pattern-based
 * reclassification suggestions.
 * 
 * Features:
 * - Suggestion badges on transactions
 * - Accept/Reject workflow
 * - Pattern confidence indicators
 * - Rationale display for court defensibility
 */

import React, { useState } from 'react';
import { Check, X, Lightbulb, ChevronDown, ChevronUp, AlertTriangle, Info } from 'lucide-react';
import api from '../../utils/api';
import { FinancialValue } from '../shared/FinancialValue';

interface PatternSuggestion {
  suggestion_id: string;
  pattern_id: string;
  transaction_id: string;
  report_id: string;
  match_score: number;
  matched_keywords: string[];
  current_category: string;
  current_subcategory?: string;
  suggested_category: string;
  suggested_subcategory: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  rationale: string;
}

interface PatternSuggestionBadgeProps {
  suggestion: PatternSuggestion;
  onAccept: () => void;
  onReject: () => void;
  compact?: boolean;
}

/**
 * Compact badge showing suggestion is available
 */
export function PatternSuggestionBadge({ 
  suggestion, 
  onAccept, 
  onReject,
  compact = true 
}: PatternSuggestionBadgeProps) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const confidenceColors: Record<'HIGH' | 'MEDIUM' | 'LOW', React.CSSProperties> = {
    HIGH: { backgroundColor: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' },
    MEDIUM: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
    LOW: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' }
  };

  const confidenceIcons = {
    HIGH: <Check size={12} style={{ color: 'var(--color-verified)' }} />,
    MEDIUM: <Info size={12} style={{ color: 'var(--color-brand)' }} />,
    LOW: <AlertTriangle size={12} style={{ color: 'var(--color-brand)' }} />
  };

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`/api/v1/patterns/${suggestion.pattern_id}/accept`, {
        transaction_id: suggestion.transaction_id,
        report_id: suggestion.report_id,
        suggestion_id: suggestion.suggestion_id
      });
      onAccept();
    } catch (error) {
      console.error('Error accepting suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`/api/v1/patterns/${suggestion.pattern_id}/reject`, {
        transaction_id: suggestion.transaction_id,
        report_id: suggestion.report_id,
        suggestion_id: suggestion.suggestion_id
      });
      onReject();
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  if (compact && !expanded) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border hover:opacity-80 transition-opacity"
        style={confidenceColors[suggestion.confidence]}
        title={`Pattern suggestion: ${suggestion.suggested_category} > ${suggestion.suggested_subcategory}`}
      >
        <Lightbulb size={12} />
        <span className="hidden sm:inline">Suggestion</span>
        {confidenceIcons[suggestion.confidence]}
      </button>
    );
  }

  return (
    <div
      className="border rounded-lg p-3"
      style={confidenceColors[suggestion.confidence]}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb size={16} style={{ color: 'var(--color-needs-human-text)' }} />
          <span className="font-medium text-sm">Pattern Suggestion</span>
          <span className="px-2 py-0.5 rounded text-xs font-medium" style={confidenceColors[suggestion.confidence]}>
            {suggestion.confidence}
          </span>
        </div>
        {compact && (
          <button
            onClick={() => setExpanded(false)}
            style={{ color: 'var(--color-text-muted)' }}
          >
            <ChevronUp size={16} />
          </button>
        )}
      </div>

      {/* Suggestion Content */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span style={{ color: 'var(--color-text-secondary)' }}>Current:</span>
          <span className="font-medium">{suggestion.current_category}</span>
          {suggestion.current_subcategory && (
            <span style={{ color: 'var(--color-text-muted)' }}>&gt; {suggestion.current_subcategory}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span style={{ color: 'var(--color-text-secondary)' }}>Suggested:</span>
          <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>{suggestion.suggested_category}</span>
          <span style={{ color: 'var(--color-needs-human-text)' }}>&gt; {suggestion.suggested_subcategory}</span>
        </div>

        {/* Rationale */}
        <div className="rounded p-2 text-xs italic" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
          "{suggestion.rationale}"
        </div>

        {/* Matched Keywords */}
        {suggestion.matched_keywords.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Matched:</span>
            {suggestion.matched_keywords.map((kw, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {kw}
              </span>
            ))}
          </div>
        )}

        {/* Match Score */}
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Match score:</span>
          <div className="flex-1 h-1.5 rounded-full max-w-[100px]" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${suggestion.match_score * 100}%`, backgroundColor: 'var(--color-brand)' }}
            />
          </div>
          <span className="text-xs font-medium">{(suggestion.match_score * 100).toFixed(0)}%</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <button
          onClick={handleAccept}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
          style={{ backgroundColor: 'var(--color-verified)', color: 'var(--var(--color-on-brand))' }}
        >
          <Check size={14} />
          Accept
        </button>
        <button
          onClick={handleReject}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
          style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
        >
          <X size={14} />
          Reject
        </button>
      </div>

      {/* Pattern ID for reference */}
      <div className="text-xs mt-2 text-center" style={{ color: 'var(--color-text-dim)' }}>
        Pattern: {suggestion.pattern_id}
      </div>
    </div>
  );
}


interface VarianceAlertProps {
  alert: {
    alert_id: string;
    alert_level: 'critical' | 'warning' | 'info';
    message: string;
    variance: number;
    variance_pct: number;
    line_item: string;
    acknowledged: boolean;
  };
  onAcknowledge: () => void;
}

/**
 * Variance alert badge for P&L cells
 */
export function VarianceAlertBadge({ alert, onAcknowledge }: VarianceAlertProps) {
  const [loading, setLoading] = useState(false);

  const alertStyles: Record<'critical' | 'warning' | 'info', React.CSSProperties> = {
    critical: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
    warning: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
    info: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' }
  };

  const alertIcons = {
    critical: <AlertTriangle size={14} style={{ color: 'var(--color-brand)' }} />,
    warning: <AlertTriangle size={14} style={{ color: 'var(--color-brand)' }} />,
    info: <Info size={14} style={{ color: 'var(--color-needs-human-text)' }} />
  };

  const handleAcknowledge = async () => {
    setLoading(true);
    try {
      await api.post(`/api/v1/alerts/${alert.alert_id}/acknowledge`);
      onAcknowledge();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      setLoading(false);
    }
  };

  if (alert.acknowledged) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border" style={alertStyles[alert.alert_level]}>
      {alertIcons[alert.alert_level]}
      <div className="flex-1">
        <div className="text-sm font-medium">{alert.message}</div>
        <div className="text-xs opacity-75">
          Variance: <FinancialValue value={Math.abs(alert.variance)} /> ({(alert.variance_pct * 100).toFixed(1)}%)
        </div>
      </div>
      <button
        onClick={handleAcknowledge}
        disabled={loading}
        className="px-2 py-1 text-xs bg-white/50 rounded hover:bg-white/80 transition-colors"
      >
        {loading ? '...' : 'Dismiss'}
      </button>
    </div>
  );
}


interface SuggestionsSummaryProps {
  transactionCount: number;
  suggestionCount: number;
  byConfidence: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  onViewAll: () => void;
}

/**
 * Summary panel showing suggestion counts
 */
export function SuggestionsSummary({ 
  transactionCount, 
  suggestionCount, 
  byConfidence,
  onViewAll 
}: SuggestionsSummaryProps) {
  if (suggestionCount === 0) return null;

  return (
    <div className="border rounded-lg p-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb size={18} style={{ color: 'var(--color-needs-human-text)' }} />
          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
            {suggestionCount} Pattern Suggestions
          </span>
          <span className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
            for {transactionCount} transactions
          </span>
        </div>
        <button
          onClick={onViewAll}
          className="text-sm font-medium"
          style={{ color: 'var(--color-needs-human-text)' }}
        >
          View All →
        </button>
      </div>
      <div className="flex gap-4 mt-2 text-sm">
        {byConfidence.HIGH > 0 && (
          <span style={{ color: 'var(--color-verified)' }}>
            <Check size={12} className="inline mr-1" />
            {byConfidence.HIGH} HIGH
          </span>
        )}
        {byConfidence.MEDIUM > 0 && (
          <span style={{ color: 'var(--color-needs-human-text)' }}>
            <Info size={12} className="inline mr-1" />
            {byConfidence.MEDIUM} MEDIUM
          </span>
        )}
        {byConfidence.LOW > 0 && (
          <span style={{ color: 'var(--color-needs-human-text)' }}>
            <AlertTriangle size={12} className="inline mr-1" />
            {byConfidence.LOW} LOW
          </span>
        )}
      </div>
    </div>
  );
}


export default PatternSuggestionBadge;
